import { z } from 'zod/v4'
import type { Shop } from '@skop-app/lib-types'

import type { ShareInfo, ShopForTasks } from '@/features/shop/shop.types'
import {
  CONNECTOR_PLATFORMS,
  CONNECTOR_UPDATE_FREQUENCIES,
  type ConnectorCredentials,
} from '@/features/shop/shop.types'

export type ShopSettingsModel = Omit<
  ShopForTasks,
  'shareInfo' | 'credentials' | 'enabled'
> & {
  shareInfo: ShareInfo | null
  credentials: ConnectorCredentials | null
  enabled: boolean | null
}

const shareInfoSchema = z
  .object({
    description: z.string().optional(),
    majorCategories: z
      .array(z.object({ label: z.string(), value: z.string() }))
      .optional(),
    majorProvenances: z
      .array(z.object({ label: z.string(), value: z.string() }))
      .optional(),
    featuredOrder: z.number().optional(),
  })
  .passthrough()

const credentialsSchema = z
  .object({
    platform: z.enum(CONNECTOR_PLATFORMS).optional(),
    apiKey: z.string().optional(),
    apiSecret: z.string().optional(),
    shopUrl: z.string().optional(),
    updateFrequency: z.enum(CONNECTOR_UPDATE_FREQUENCIES).optional(),
    partner: z.string().optional(),
    removeVAT: z.boolean().optional(),
    categories: z
      .array(
        z.object({ selected: z.boolean(), id: z.string(), name: z.string() }),
      )
      .optional(),
    customFields: z.record(z.string(), z.string()).optional(),
  })
  .passthrough()

export const parseShareInfo = (raw: unknown): ShareInfo | null => {
  if (!raw) return null
  let value: unknown = raw
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw)
    } catch (error) {
      console.error('Failed to parse shareInfo:', error)
      return null
    }
  }
  const result = shareInfoSchema.safeParse(value)
  return result.success ? result.data : null
}

export const parseCredentials = (raw: unknown): ConnectorCredentials | null => {
  if (!raw) return null
  let value: unknown = raw
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw)
    } catch (error) {
      console.error('Failed to parse credentials:', error)
      return null
    }
  }
  const result = credentialsSchema.safeParse(value)
  return result.success ? result.data : null
}

export const serializeShareInfo = (input: ShareInfo): string => {
  return JSON.stringify(input)
}

export const serializeCredentials = (input: ConnectorCredentials): string => {
  return JSON.stringify(input)
}

export const normalizeEnabled = (
  raw: Shop['enabled'] | boolean | null | undefined,
) => {
  if (raw === true || raw === false) return raw
  if (raw === 'true') return true
  if (raw === 'false') return false
  return null
}

export const normalizeShop = (
  rawShop: ShopForTasks | Shop | null | undefined,
): ShopSettingsModel | null => {
  if (!rawShop) return null
  const shareInfo = parseShareInfo(rawShop.shareInfo)
  const credentials = parseCredentials(rawShop.credentials)
  const enabled = normalizeEnabled(rawShop.enabled)

  return {
    ...rawShop,
    shareInfo,
    credentials,
    enabled,
  }
}
